import {
  BookFilled,
  BookOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  GlobalOutlined,
  LikeFilled,
  LikeOutlined,
  MoreOutlined,
  PictureOutlined,
  SmileOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons'
import {
  Avatar,
  Dropdown,
  List,
  Menu,
  message,
  Modal,
  Popover,
  Progress,
  Select,
  Spin,
  Tabs,
  Tag,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import SelectPeoplePopup from '../../../../components/SelectPeoplePopup/SelectPeoplePopup'
import { apiUrl } from '../../../../config'
import { FileRequest } from '../../../../requests/FileRequest'
import utils from '../../../../utils'
import {
  ActionButton,
  colors,
  PostActions,
  PostAuthor,
  PostAuthorName,
  PostCardContainer,
  PostCardHeader,
  PostContent,
  PostMedia,
  PostMenuBtn,
  PostMeta,
  PostStats,
  PostTime,
  ReactionsModal,
  StatComments,
  StatLikeIcon,
  StatLikes,
  UserAvatar,
} from '../../NewsFeedStyled'
import CommentSection from '../CommentSection/CommentSection'
import ComposerMediaEditor from '../ComposerMediaEditor/ComposerMediaEditor'
import MediaGrid from '../MediaGrid/MediaGrid'
import UserProfilePopover from '../UserProfilePopover'
import NewsFeedEditor from '../NewsFeedEditor/NewsFeedEditor'
import FeelingSelectModal from '../FeelingSelectModal/FeelingSelectModal'
import {
  REACTIONS,
  ReactionIcon,
  extractPostContentAndFeeling,
  renderFormattedContent,
  isHtmlContent,
  stripHtml,
} from '../../constants/newsFeedConstants'
const { TabPane } = Tabs

const ReactionPopup = styled.div`
  position: absolute;
  bottom: 100%;
  left: 10px;
  background: #ffffff;
  border-radius: 24px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e4e6eb;
  z-index: 1000;
  transform: translateY(-8px);
  animation: reactionPopup 0.2s cubic-bezier(0.18, 0.89, 0.32, 1.28) both;

  @keyframes reactionPopup {
    from {
      opacity: 0;
      transform: translateY(10px) scale(0.8);
    }
    to {
      opacity: 1;
      transform: translateY(-8px) scale(1);
    }
  }
`

const ReactionEmoji = styled.div`
  font-size: 24px;
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.18, 0.89, 0.32, 1.28);
  padding: 4px;
  user-select: none;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.35) translateY(-4px);
    background: #f1f5f9;
  }
`

const { confirm } = Modal

/** Format thời gian tương đối kiểu Facebook */
const formatTimeAgo = dateStr => {
  if (!dateStr) return ''
  const now = moment()
  const date = moment(dateStr)
  const diffMinutes = now.diff(date, 'minutes')
  const diffHours = now.diff(date, 'hours')
  const diffDays = now.diff(date, 'days')

  if (diffMinutes < 1) return 'Vừa xong'
  if (diffMinutes < 60) return `${diffMinutes} phút trước`
  if (diffHours < 24) return `${diffHours} giờ trước`
  if (diffDays < 7) return `${diffDays} ngày trước`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} tuần trước`
  return date.format('DD/MM/YYYY [lúc] HH:mm')
}

/** Resolve avatar URL - prepend apiUrl for relative paths */
const resolveAvatarUrl = url => {
  if (!url) return null
  if (url.startsWith('http')) return url
  return `${apiUrl}${url}`
}

/** Format name to Capitalize First Letters */
const formatName = name => {
  if (!name) return ''
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const renderTextWithLinks = text => {
  if (typeof text !== 'string') return text
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = text.split(urlRegex)
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1877f2' }}
          onClick={e => e.stopPropagation()}>
          {part}
        </a>
      )
    }
    return part
  })
}

const renderPostContent = content => {
  if (!content) return ''

  // Use brackets to delimit mentions safely across backends
  const regex = /(@\[[^\]]+\])/g

  const parts = content.split(regex)
  if (parts.length > 1) {
    return parts.map((part, index) => {
      if (part.startsWith('@[') && part.endsWith(']')) {
        return (
          <span
            key={index}
            style={{
              color: '#050505',
              background: 'rgba(24, 119, 242, 0.12)',
              borderRadius: 12,
              padding: '2px 6px',
              fontWeight: 600,
              cursor: 'pointer',
            }}>
            {part.substring(2, part.length - 1)}
          </span>
        )
      }
      return (
        <React.Fragment key={index}>{renderTextWithLinks(part)}</React.Fragment>
      )
    })
  }

  // Fallback for old greedy formatting if no zero-width space was found
  const oldRegex = /(@[A-ZĐĂÂÊÔƠƯÀẢÃÁẠẰẲẴẮẶẦẨẪẤẬỀỂỄẾỆỒỔỖỐỘỜỞỠỚỢÙỦŨÚỤỪỬỮỨỰỲỶỸÝa-zđăâêôơưàảãáạằẳẵắặầẩẫấậềểễếệồổỗốộờởỡớợùủũúụừửữứựỳỷỹý]+(?:\s+[A-ZĐĂÂÊÔƠƯÀẢÃÁẠẰẲẴẮẶẦẨẪẤẬỀỂỄẾỆỒỔỖỐỘỜỞỠỚỢÙỦŨÚỤỪỬỮỨỰỲỶỸÝa-zđăâêôơưàảãáạằẳẵắặầẩẫấậềểễếệồổỗốộờởỡớợùủũúụừửữứựỳỷỹý]+)*)/g
  const oldParts = content.split(oldRegex)
  return oldParts.map((part, index) => {
    if (part.startsWith('@')) {
      return (
        <span
          key={index}
          style={{ color: '#1877f2', fontWeight: 600, cursor: 'pointer' }}>
          {part.substring(1)}
        </span>
      )
    }
    return (
      <React.Fragment key={index}>{renderTextWithLinks(part)}</React.Fragment>
    )
  })
}

const getReactionKeyFromUser = user => {
  return (
    user?.reaction ||
    user?.reaction_type ||
    user?.reactionKey ||
    user?.reaction_key ||
    user?.reactionCode ||
    user?.reaction_code ||
    null
  )
}

const PostCard = ({
  post,
  currentUser,
  onNavigateToPost,
  newsFeedStore,
  authenticationStore,
  userStore,
  selectPeopleStore,
  isBookmarksTab,
  defaultShowComments = false,
}) => {
  const [showComments, setShowComments] = useState(defaultShowComments)
  const [isTextExpanded, setIsTextExpanded] = useState(false)
  const [likesModalVisible, setLikesModalVisible] = useState(false)
  const [likedUsers, setLikedUsers] = useState([])
  const [loadingLikes, setLoadingLikes] = useState(false)
  const [activeReactionTab, setActiveReactionTab] = useState('ALL')
  const [localLikeCount, setLocalLikeCount] = useState(post.like_count || 0)
  const [localIsLiked, setLocalIsLiked] = useState(Boolean(post.is_liked))

  // Hover reactions states
  const [showReactions, setShowReactions] = useState(false)
  const [closeTimeoutId, setCloseTimeoutId] = useState(null)
  const [localReaction, setLocalReaction] = useState(null)

  useEffect(() => {
    setLocalLikeCount(post.like_count || 0)
    setLocalIsLiked(Boolean(post.is_liked))
  }, [post.id, post.like_count, post.is_liked])

  // Edit post states
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [editText, setEditText] = useState('')
  const [editVisibility, setEditVisibility] = useState('PUBLIC')
  const [editMediaFiles, setEditMediaFiles] = useState([])
  const [editTaggedUsers, setEditTaggedUsers] = useState([])
  const [editCustomViewers, setEditCustomViewers] = useState([])
  const [isEditSubmitting, setIsEditSubmitting] = useState(false)
  const [editUploadProgress, setEditUploadProgress] = useState(0)

  const [showEditEmojiPicker, setShowEditEmojiPicker] = useState(false)
  const [showEditSelectPeople, setShowEditSelectPeople] = useState(false)
  const [showEditSelectPeopleTagged, setShowEditSelectPeopleTagged] = useState(
    false
  )
  const [editFeeling, setEditFeeling] = useState(null)
  const [showEditFeelingModal, setShowEditFeelingModal] = useState(false)
  const [reactionsList, setReactionsList] = useState([])

  const { content: cleanContent, feeling: postFeeling } = useMemo(() => {
    return extractPostContentAndFeeling(post.content, post.feeling)
  }, [post.content, post.feeling])

  const editFileInputRef = useRef(null)


  // Connect SSE for real-time likes
  useEffect(() => {
    newsFeedStore.connectSSE(post.id)
    return () => {
      newsFeedStore.disconnectSSE(post.id)
    }
  }, [post.id, newsFeedStore])

  // Helper to resolve tagged user display name
  const getTaggedUserDisplayName = useCallback(
    username => {
      if (post.tagged_user_profiles) {
        const profile = post.tagged_user_profiles.find(
          p => p.username === username
        )
        if (
          profile &&
          profile.display_name &&
          profile.display_name !== profile.username
        ) {
          return formatName(profile.display_name)
        }
      }

      const allUsers = [
        ...(userStore?.mentionUserList || []),
        ...(userStore?.userList || []),
      ]
      const foundUser = allUsers.find(
        u =>
          (u.username && u.username.toLowerCase() === username.toLowerCase()) ||
          (u.code && String(u.code).toLowerCase() === username.toLowerCase()) ||
          (u.id && String(u.id).toLowerCase() === username.toLowerCase())
      )
      if (foundUser) {
        if (foundUser.display) return formatName(foundUser.display)
        if (foundUser.name_uppercase)
          return formatName(foundUser.name_uppercase)
        if (foundUser.full_name) return formatName(foundUser.full_name)
        if (foundUser.fullName) return formatName(foundUser.fullName)
      }
      return formatName(username)
    },
    [post.tagged_user_profiles, userStore?.mentionUserList, userStore?.userList]
  )

  const handleEditFileSelect = useCallback(e => {
    const files = Array.from(e.target.files)
    const newMedia = files.map(file => {
      let type = 'DOCUMENT'
      if (file.type.startsWith('image/')) {
        type = 'IMAGE'
      } else if (file.type.startsWith('video/')) {
        type = 'VIDEO'
      } else if (file.type === 'application/pdf') {
        type = 'PDF'
      }
      return {
        file,
        preview:
          file.type.startsWith('image/') || file.type.startsWith('video/')
            ? URL.createObjectURL(file)
            : null,
        type,
        isNew: true,
        fileName: file.name,
        id: Date.now() + Math.random(),
        caption: '',
      }
    })
    setEditMediaFiles(prev => [...prev, ...newMedia])
    e.target.value = ''
  }, [])

  const handleOpenEditModal = useCallback(() => {
    const { content: cleanText, feeling: existingFeeling } =
      extractPostContentAndFeeling(post.content, post.feeling)
    setEditText(cleanText || '')
    setEditFeeling(existingFeeling)
    setEditVisibility(post.visibility || 'PUBLIC')
    setEditTaggedUsers(post.tagged_users || post.taggedUsers || [])
    setEditCustomViewers([])

    const existingMedia = (post.media || []).map(m => {
      const fileId = m.fileId || m.file_id
      const mediaType = m.mediaType || m.media_type
      return {
        fileId,
        fileName: m.fileName || m.file_name,
        fileUrl: m.fileUrl || m.file_url,
        type: mediaType,
        isNew: false,
        caption: m.caption || '',
        id: m.id || fileId,
        preview: fileId ? `${apiUrl}/api/v1/images/${fileId}` : null,
      }
    })
    setEditMediaFiles(existingMedia)

    if (selectPeopleStore) {
      const initialTaggedUsers = (
        post.tagged_users ||
        post.taggedUsers ||
        []
      ).map(username => {
        const found = userStore?.mentionUserList?.find(
          u =>
            (u.username &&
              u.username.toLowerCase() === username.toLowerCase()) ||
            (u.id && String(u.id).toLowerCase() === username.toLowerCase())
        )
        return {
          id: username,
          username: username,
          display: found?.display || username,
          full_name: found?.display || username,
          name_uppercase: (found?.display || username).toUpperCase(),
        }
      })
      selectPeopleStore.selectUserData['TAGGED_USERS'] = initialTaggedUsers
    }

    setIsEditModalVisible(true)
  }, [post, selectPeopleStore, userStore])

  const handleEditSubmit = useCallback(async () => {
    try {
      setIsEditSubmitting(true)
      setEditUploadProgress(0)

      const fileIds = []
      const mediaCaptions = []

      // Keep order of editMediaFiles; upload new files in place
      let uploadedCount = 0
      const newFiles = editMediaFiles.filter(m => m.isNew)
      for (const m of editMediaFiles) {
        if (!m.isNew && m.fileId) {
          fileIds.push(m.fileId)
          mediaCaptions.push((m.caption || '').trim())
          continue
        }
        if (m.isNew && m.file) {
          const fileFormData = new FormData()
          fileFormData.append('file', m.file)
          const uploadRes = await FileRequest.uploadFile(fileFormData)
          if (
            uploadRes &&
            uploadRes.data &&
            (uploadRes.data.file_id || uploadRes.data.fileId)
          ) {
            const fId = uploadRes.data.file_id || uploadRes.data.fileId
            fileIds.push(fId)
            mediaCaptions.push((m.caption || '').trim())
          } else {
            throw new Error(`Upload ${m.fileName} thất bại`)
          }
          uploadedCount++
          setEditUploadProgress(
            Math.floor((uploadedCount / Math.max(newFiles.length, 1)) * 50)
          )
        }
      }

      const formData = new FormData()
      let finalContent = editText.trim()
      if (editFeeling) {
        finalContent = `<!--feeling:${JSON.stringify(
          editFeeling
        )}-->\n${finalContent}`
      }
      formData.append('content', finalContent)
      formData.append('visibility', editVisibility)
      if (editFeeling) {
        formData.append('feeling', JSON.stringify(editFeeling))
      }

      if (editVisibility === 'CUSTOM' && editCustomViewers.length > 0) {
        editCustomViewers.forEach(v => {
          formData.append('customViewers', v)
        })
      }

      if (editTaggedUsers.length > 0) {
        editTaggedUsers.forEach(v => {
          formData.append('taggedUsers', v)
        })
      }

      if (fileIds.length > 0) {
        fileIds.forEach(id => formData.append('fileIds', id))
        mediaCaptions.forEach(caption =>
          formData.append('mediaCaptions', caption)
        )
      }

      await newsFeedStore.updatePost(post.id, formData)

      message.success('Cập nhật bài viết thành công')
      setIsEditModalVisible(false)

      newFiles.forEach(m => {
        if (m.preview) URL.revokeObjectURL(m.preview)
      })
    } catch (err) {
      console.error('Update post error:', err)
      message.error(err?.vi || 'Cập nhật bài viết thất bại!')
    } finally {
      setIsEditSubmitting(false)
      setEditUploadProgress(0)
    }
  }, [
    editText,
    editFeeling,
    editVisibility,
    editCustomViewers,
    editTaggedUsers,
    editMediaFiles,
    post.id,
    newsFeedStore,
  ])

  // Hiển thị trạng thái cảm xúc và người được gắn thẻ cạnh tên tác giả theo format:
  // "VIMClog News - cảm thấy tự hào cùng Đinh Thị Việt Hà"
  const renderFeelingAndTagged = () => {
    const tags = post.tagged_users || post.taggedUsers || []
    const hasFeeling = Boolean(
      postFeeling && (postFeeling.text || postFeeling.label)
    )
    const feelingText =
      postFeeling?.text ||
      (postFeeling?.label ? `cảm thấy ${postFeeling.label.toLowerCase()}` : '')
    const feelingEmoji = postFeeling?.emoji || ''

    if (!hasFeeling && tags.length === 0) return null

    const renderSingleUser = username => (
      <strong
        key={username}
        onClick={e => e.stopPropagation()}
        style={{
          color: '#050505',
          cursor: 'pointer',
          textDecoration: 'underline',
          fontWeight: 600,
        }}>
        {getTaggedUserDisplayName(username)}
      </strong>
    )

    const renderTaggedList = () => {
      if (tags.length === 0) return null

      if (tags.length === 1) {
        return renderSingleUser(tags[0])
      }

      if (tags.length === 2) {
        return (
          <>
            {renderSingleUser(tags[0])} và {renderSingleUser(tags[1])}
          </>
        )
      }

      const otherCount = tags.length - 1
      const popoverContent = (
        <div style={{ maxHeight: 200, overflowY: 'auto', padding: '4px 6px' }}>
          {tags.slice(1).map(username => (
            <div
              key={username}
              style={{ padding: '3px 0', fontWeight: 600, fontSize: 13 }}>
              {getTaggedUserDisplayName(username)}
            </div>
          ))}
        </div>
      )

      return (
        <>
          {renderSingleUser(tags[0])} và{' '}
          <Popover content={popoverContent} trigger="hover">
            <span
              onClick={e => e.stopPropagation()}
              style={{
                color: '#1877f2',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}>
              {otherCount} người khác
            </span>
          </Popover>
        </>
      )
    }

    return (
      <span
        style={{
          fontSize: 14,
          color: '#65676b',
          fontWeight: 'normal',
          display: 'inline-flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '4px',
        }}>
        {hasFeeling && (
          <>
            <span>{' '}</span>
            {feelingEmoji && (
              <span style={{ fontSize: 15 }}>{feelingEmoji}</span>
            )}
            <span style={{ color: '#65676b' }}>{feelingText}</span>
          </>
        )}
        {tags.length > 0 && (
          <>
            <span style={{ color: '#65676b' }}>cùng</span>
            {renderTaggedList()}
          </>
        )}
      </span>
    )
  }

  const handleMouseEnter = () => {
    if (closeTimeoutId) {
      clearTimeout(closeTimeoutId)
      setCloseTimeoutId(null)
    }
    setShowReactions(true)
  }

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowReactions(false)
    }, 400)
    setCloseTimeoutId(timeout)
  }

  const currentUserReaction = useMemo(() => {
    if (!currentUser?.username || reactionsList.length === 0) return null
    const me = reactionsList.find(
      user =>
        user?.userCode?.toLowerCase() === currentUser.username.toLowerCase() ||
        user?.user_code?.toLowerCase() === currentUser.username.toLowerCase()
    )
    const reactionKey = getReactionKeyFromUser(me)
    return REACTIONS.find(r => r.key === reactionKey) || null
  }, [currentUser, reactionsList])

  const activeReaction = useMemo(() => {
    if (!localIsLiked) return null
    if (localReaction) return localReaction
    const storedReact = localStorage.getItem(`post-react-${post.id}`)
    if (storedReact) {
      const found = REACTIONS.find(r => r.key === storedReact)
      if (found) return found
    }
    if (currentUserReaction) return currentUserReaction
    return null
  }, [localIsLiked, post.id, localReaction, currentUserReaction])

  const fetchLikes = useCallback(() => {
    newsFeedStore
      .getPostLikes(post.id)
      .then(users => {
        setReactionsList(users)
      })
      .catch(err => {
        console.error('Failed to load post likes', err)
      })
  }, [post.id, newsFeedStore])

  useEffect(() => {
    if (post.like_count > 0) {
      fetchLikes()
    } else {
      setReactionsList([])
    }
  }, [post.id, post.like_count, post.reaction_updated_at, fetchLikes])

  const postReactions = useMemo(() => {
    if (reactionsList.length === 0) return []

    // Deduplicate users by userCode / user_code / id to avoid counting same user twice
    const seen = new Map()
    for (const user of reactionsList) {
      const code = (
        user.userCode ||
        user.user_code ||
        user.username ||
        String(user.id || '')
      )
        .toString()
        .toLowerCase()
      if (!code) continue
      if (seen.has(code)) continue
      seen.set(code, user)
    }

    const counts = {}
    for (const user of seen.values()) {
      let rKey = getReactionKeyFromUser(user)
      if (
        !rKey &&
        (user.userCode || user.user_code) === currentUser?.username
      ) {
        rKey = localStorage.getItem(`post-react-${post.id}`) || 'LIKE'
      }
      if (!rKey) rKey = 'LIKE'
      counts[rKey] = (counts[rKey] || 0) + 1
    }

    const mapped = Object.keys(counts).map(key => {
      const reactionObj = REACTIONS.find(r => r.key === key) || REACTIONS[0]
      return { key, count: counts[key], reactionObj }
    })

    mapped.sort((a, b) => b.count - a.count)
    return mapped.slice(0, 3)
  }, [reactionsList, currentUser, post.id])

  const handleSelectReaction = useCallback(
    reactionKey => {
      const reaction = REACTIONS.find(r => r.key === reactionKey)
      const reactionChanged = activeReaction?.key !== reactionKey
      const shouldLike = !localIsLiked

      localStorage.setItem(`post-react-${post.id}`, reactionKey)
      setLocalReaction(reaction)
      setShowReactions(false)
      if (shouldLike) {
        setLocalIsLiked(true)
        setLocalLikeCount(prev => prev + 1)
      }

      if (shouldLike || reactionChanged) {
        newsFeedStore
          .toggleLikePost(post.id, reactionKey)
          .then(() => {
            fetchLikes()
          })
          .catch(() => {
            message.error('Không thể thực hiện thao tác!')
            setLocalReaction(null)
            if (shouldLike) {
              setLocalIsLiked(false)
              setLocalLikeCount(prev => Math.max(0, prev - 1))
            }
          })
      } else {
        fetchLikes()
      }
    },
    [post.id, localIsLiked, activeReaction?.key, newsFeedStore, fetchLikes]
  )

  const handleLikeClick = useCallback(() => {
    if (localIsLiked) {
      localStorage.removeItem(`post-react-${post.id}`)
      setLocalReaction(null)
      setLocalIsLiked(false)
      setLocalLikeCount(prev => Math.max(0, prev - 1))
      newsFeedStore
        .toggleLikePost(post.id, null)
        .then(() => {
          fetchLikes()
        })
        .catch(() => {
          message.error('Không thể thực hiện thao tác!')
          setLocalIsLiked(true)
          setLocalLikeCount(prev => prev + 1)
        })
    } else {
      localStorage.setItem(`post-react-${post.id}`, 'LIKE')
      setLocalReaction(REACTIONS[0])
      setLocalIsLiked(true)
      setLocalLikeCount(prev => prev + 1)
      newsFeedStore
        .toggleLikePost(post.id, 'LIKE')
        .then(() => {
          fetchLikes()
        })
        .catch(() => {
          message.error('Không thể thực hiện thao tác!')
          setLocalReaction(null)
          setLocalIsLiked(false)
          setLocalLikeCount(prev => Math.max(0, prev - 1))
          localStorage.removeItem(`post-react-${post.id}`)
        })
    }
  }, [post.id, localIsLiked, currentUser, newsFeedStore, fetchLikes])

  const handleShowLikesModal = useCallback(() => {
    setLikesModalVisible(true)
    setLoadingLikes(true)
    newsFeedStore
      .getPostLikes(post.id)
      .then(users => {
        setLikedUsers(users)
      })
      .catch(() => {
        message.error('Không thể lấy danh sách lượt thích!')
      })
      .finally(() => {
        setLoadingLikes(false)
        setActiveReactionTab('ALL')
      })
  }, [post.id, newsFeedStore])

  const groupedReactions = useMemo(() => {
    const groups = { ALL: [] }
    // Deduplicate likedUsers similarly to avoid duplicate entries
    const seen = new Map()
    for (const user of likedUsers) {
      const code = (
        user.userCode ||
        user.user_code ||
        user.username ||
        String(user.id || '')
      )
        .toString()
        .toLowerCase()
      if (!code) continue
      if (seen.has(code)) continue
      seen.set(code, user)
    }

    for (const user of seen.values()) {
      let rKey = getReactionKeyFromUser(user)
      if (
        !rKey &&
        (user.userCode || user.user_code) === currentUser?.username
      ) {
        rKey = localStorage.getItem(`post-react-${post.id}`) || 'LIKE'
      }
      if (!rKey) rKey = 'LIKE'
      const reactionObj = REACTIONS.find(r => r.key === rKey) || REACTIONS[0]
      const enrichedUser = { ...user, reactionKey: rKey, reactionObj }
      groups.ALL.push(enrichedUser)
      if (!groups[rKey]) groups[rKey] = []
      groups[rKey].push(enrichedUser)
    }
    return groups
  }, [likedUsers, currentUser, post.id])

  const currentTabUsers = groupedReactions[activeReactionTab] || []

  const isOwner = currentUser?.username === post.user_code
  const isAdmin =
    authenticationStore?.isAccountAdmin || authenticationStore?.isSuperAdmin

  // Truncate text
  const MAX_VISIBLE_CHARS = 300
  const isHtml = isHtmlContent(cleanContent)
  const plainTextLength = isHtml
    ? stripHtml(cleanContent).length
    : cleanContent?.length || 0
  const shouldTruncate = plainTextLength > MAX_VISIBLE_CHARS
  const displayText = useMemo(() => {
    if (!cleanContent) return ''
    if (isHtml) return cleanContent
    if (!shouldTruncate || isTextExpanded) return cleanContent
    return cleanContent.substring(0, MAX_VISIBLE_CHARS) + '...'
  }, [cleanContent, isHtml, shouldTruncate, isTextExpanded])

  const avatarSrc = resolveAvatarUrl(post.avatar_url)

  // Bookmark handler
  const handleBookmark = useCallback(() => {
    newsFeedStore
      .toggleBookmarkPost(post.id, isBookmarksTab)
      .then(() => {
        message.success(
          post.is_bookmarked ? 'Đã lưu bài viết' : 'Đã bỏ lưu bài viết'
        )
      })
      .catch(() => {
        message.error('Không thể thực hiện thao tác!')
      })
  }, [post.id, post.is_bookmarked, isBookmarksTab])

  // Delete handler
  const handleDelete = useCallback(() => {
    confirm({
      title: 'Xác nhận xóa bài viết',
      icon: <ExclamationCircleOutlined />,
      content: 'Bài viết sẽ bị xóa vĩnh viễn. Bạn có chắc chắn?',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await newsFeedStore.deletePost(post.id)
          message.success('Đã xóa bài viết')
        } catch (err) {
          message.error(err?.vi || 'Không thể xóa bài viết!')
        }
      },
    })
  }, [post.id])

  // Hide handler
  const handleHide = useCallback(() => {
    confirm({
      title: 'Ẩn bài viết',
      icon: <EyeInvisibleOutlined />,
      content: (
        <div>
          <p>
            Bài viết này sẽ bị ẩn khỏi bảng tin và không còn hiển thị với người
            dùng khác.
          </p>

          <p>
            Sau khi ẩn, bài viết sẽ không xuất hiện trong danh sách bài viết
            công khai, tuy nhiên dữ liệu bài viết vẫn được lưu trong hệ thống và
            bạn có thể mở ẩn lại bất cứ lúc nào.
          </p>

          <p style={{ marginBottom: 0 }}>
            Bạn có chắc chắn muốn ẩn bài viết này không?
          </p>
        </div>
      ),
      okText: 'Ẩn bài viết',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await newsFeedStore.hidePost(post.id)
          message.success('Đã ẩn bài viết thành công')
        } catch (err) {
          message.error(err?.vi || 'Không thể ẩn bài viết!')
        }
      },
    })
  }, [post.id])

  // Unhide handler
  const handleUnhide = useCallback(() => {
    confirm({
      title: 'Mở ẩn bài viết',
      icon: <EyeInvisibleOutlined />,
      content: (
        <div>
          <p>
            Bài viết này hiện đang bị ẩn khỏi bảng tin và người dùng khác không
            thể nhìn thấy.
          </p>

          <p>
            Sau khi mở ẩn, bài viết sẽ được hiển thị trở lại trên bảng tin, xuất
            hiện trong danh sách bài viết và người dùng có quyền truy cập sẽ
            tiếp tục xem được nội dung này.
          </p>

          <p style={{ marginBottom: 0 }}>
            Bạn có chắc chắn muốn hiển thị lại bài viết này không?
          </p>
        </div>
      ),
      okText: 'Hiển thị lại',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await newsFeedStore.unhidePost(post.id)
          message.success('Đã mở ẩn bài viết thành công')
        } catch (err) {
          message.error(err?.vi || 'Không thể mở ẩn bài viết!')
        }
      },
    })
  }, [post.id])

  // Post menu
  const postMenu = (
    <Menu>
      <Menu.Item
        key="bookmark"
        icon={
          post.is_bookmarked ? (
            <BookFilled style={{ color: colors.primary }} />
          ) : (
            <BookOutlined />
          )
        }
        onClick={handleBookmark}>
        {post.is_bookmarked ? 'Bỏ lưu bài viết' : 'Lưu bài viết'}
      </Menu.Item>
      {(isOwner || isAdmin) && (
        <>
          <Menu.Divider />
          {isOwner && (
            <Menu.Item
              key="edit"
              icon={<EditOutlined />}
              onClick={handleOpenEditModal}>
              Chỉnh sửa bài viết
            </Menu.Item>
          )}
          {post.status === 'HIDDEN' ? (
            <Menu.Item
              key="unhide"
              icon={<EyeInvisibleOutlined style={{ color: colors.primary }} />}
              onClick={handleUnhide}>
              Mở ẩn bài viết
            </Menu.Item>
          ) : (
            <Menu.Item
              key="hide"
              icon={<EyeInvisibleOutlined />}
              onClick={handleHide}>
              Ẩn bài viết
            </Menu.Item>
          )}
          <Menu.Item
            key="delete"
            icon={<DeleteOutlined />}
            danger
            onClick={handleDelete}>
            Xóa bài viết
          </Menu.Item>
        </>
      )}
    </Menu>
  )

  return (
    <PostCardContainer id={`post-${post.id}`}>
      {/* Post Header */}
      <PostCardHeader>
        <UserProfilePopover
          userCode={post.user_code}
          displayName={post.display_name}
          avatarUrl={post.avatar_url}>
          <PostAuthor>
            <UserAvatar>
              {avatarSrc ? (
                <img src={avatarSrc} alt="" />
              ) : (
                <span>{(formatName(post.display_name) || 'U').charAt(0)}</span>
              )}
            </UserAvatar>
            <PostMeta>
              <PostAuthorName
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '4px',
                }}>
                <span
                  onClick={e => e.stopPropagation()}
                  style={{ cursor: 'pointer' }}>
                  {formatName(post.display_name) || 'Người dùng'}
                </span>
                {renderFeelingAndTagged()}
              </PostAuthorName>
              <PostTime>
                {formatTimeAgo(post.created_at)}
                <span>
                  {post.visibility === 'PUBLIC'
                    ? ' · 🌐'
                    : post.visibility === 'COMPANY'
                    ? ' · 🏢'
                    : ' · 🔒'}
                </span>
              </PostTime>
            </PostMeta>
          </PostAuthor>
        </UserProfilePopover>

        <Dropdown
          overlay={postMenu}
          trigger={['click']}
          placement="bottomRight">
          <PostMenuBtn>
            <MoreOutlined />
          </PostMenuBtn>
        </Dropdown>
      </PostCardHeader>

      {/* Post Content */}
      {cleanContent && (
        <PostContent>
          {isHtml ? (
            <div
              style={{
                position: 'relative',
                maxHeight: shouldTruncate && !isTextExpanded ? 180 : 'none',
                overflow: 'hidden',
                WebkitMaskImage:
                  shouldTruncate && !isTextExpanded
                    ? 'linear-gradient(180deg, #000 65%, transparent)'
                    : 'none',
                maskImage:
                  shouldTruncate && !isTextExpanded
                    ? 'linear-gradient(180deg, #000 65%, transparent)'
                    : 'none',
                transition: 'max-height 0.25s ease',
              }}
              dangerouslySetInnerHTML={{ __html: cleanContent }}
            />
          ) : (
            <p
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.6,
              }}>
              {renderFormattedContent(displayText)}
            </p>
          )}
          {shouldTruncate && (
            <button
              style={{
                border: 'none',
                background: 'transparent',
                color: colors.primary,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
                padding: 0,
                marginTop: 4,
              }}
              onClick={() => setIsTextExpanded(!isTextExpanded)}>
              {isTextExpanded ? 'Ẩn bớt' : 'Xem thêm'}
            </button>
          )}
        </PostContent>
      )}

      {/* Post Media */}
      {post.media && post.media.length > 0 && (
        <PostMedia>
          <MediaGrid media={post.media} post={post} />
        </PostMedia>
      )}

      {/* Like & Comment Count */}
      <PostStats>
        {localLikeCount > 0 && (
          <StatLikes
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={handleShowLikesModal}>
            <div
              style={{ display: 'flex', alignItems: 'center', marginRight: 4 }}>
              {postReactions.length > 0 ? (
                postReactions.map((r, i) => (
                  <span
                    key={r.key}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginLeft: i > 0 ? -4 : 0,
                      zIndex: 3 - i,
                      position: 'relative',
                    }}>
                    <ReactionIcon type={r.key} size={18} />
                  </span>
                ))
              ) : activeReaction ? (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <ReactionIcon type={activeReaction.key} size={18} />
                </span>
              ) : (
                <StatLikeIcon>
                  <ReactionIcon type="LIKE" size={16} />
                </StatLikeIcon>
              )}
            </div>
            <span>{localLikeCount}</span>
          </StatLikes>
        )}
        {post.comment_count > 0 && (
          <StatComments onClick={() => setShowComments(!showComments)}>
            {post.comment_count} bình luận
          </StatComments>
        )}
      </PostStats>

      {/* Action Buttons */}
      <PostActions style={{ position: 'relative' }}>
        <div
          style={{ flex: 1, display: 'flex', position: 'relative' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          {showReactions && (
            <ReactionPopup>
              {REACTIONS.map(react => (
                <ReactionEmoji
                  key={react.key}
                  onClick={() => handleSelectReaction(react.key)}
                  title={react.label}>
                  <ReactionIcon type={react.key} size={30} />
                </ReactionEmoji>
              ))}
            </ReactionPopup>
          )}
          <ActionButton
            className={localIsLiked ? 'liked' : ''}
            onClick={handleLikeClick}
            style={{
              color: activeReaction ? activeReaction.color : undefined,
              width: '100%',
            }}>
            {activeReaction ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginRight: 6,
                }}>
                <ReactionIcon type={activeReaction.key} size={20} />
              </span>
            ) : (
              <LikeOutlined />
            )}
            <span style={{ fontWeight: activeReaction ? '700' : '600' }}>
              {activeReaction ? activeReaction.label : 'Thích'}
            </span>
          </ActionButton>
        </div>
        <ActionButton onClick={() => setShowComments(!showComments)}>
          <CommentOutlined />
          <span>Bình luận</span>
        </ActionButton>
      </PostActions>

      {/* Comment Section */}
      {showComments && (
        <CommentSection postId={post.id} currentUser={currentUser} />
      )}

      {/* Likes Modal */}
      <ReactionsModal
        visible={likesModalVisible}
        onCancel={() => setLikesModalVisible(false)}
        footer={null}
        width={450}
        bodyStyle={{ padding: '0', display: 'flex', flexDirection: 'column' }}
        destroyOnClose
        closable={true}
        title={null}>
        <div
          style={{
            padding: '12px 16px 0 16px',
            borderBottom: '1px solid #e4e6eb',
          }}>
          <Tabs
            activeKey={activeReactionTab}
            onChange={setActiveReactionTab}
            size="large"
            tabBarStyle={{ margin: 0, borderBottom: 'none' }}>
            <TabPane
              tab={
                <span style={{ fontWeight: 600 }}>
                  Tất cả {groupedReactions.ALL.length}
                </span>
              }
              key="ALL"
            />
            {Object.keys(groupedReactions)
              .filter(k => k !== 'ALL')
              .map(rKey => {
                const rObj = REACTIONS.find(r => r.key === rKey)
                if (!rObj) return null
                return (
                  <TabPane
                    tab={
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontWeight: 600,
                          color:
                            activeReactionTab === rKey ? rObj.color : undefined,
                        }}>
                        <ReactionIcon type={rKey} size={18} />{' '}
                        {groupedReactions[rKey].length}
                      </span>
                    }
                    key={rKey}
                  />
                )
              })}
          </Tabs>
        </div>
        <div
          className="reactions-modal-list"
          style={{ padding: '8px 16px', overflowY: 'auto', maxHeight: 400 }}>
          {loadingLikes ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '24px 0',
              }}>
              <Spin size="medium" />
            </div>
          ) : currentTabUsers.length === 0 ? (
            <div
              style={{ textAlign: 'center', color: '#888', padding: '24px 0' }}>
              Chưa có lượt thích nào.
            </div>
          ) : (
            <List
              dataSource={currentTabUsers}
              renderItem={item => {
                const displayName = formatName(
                  item.displayName || item.userCode
                )
                const foundUser = userStore?.mentionUserList?.find(
                  u =>
                    u.id && u.id.toLowerCase() === item.userCode?.toLowerCase()
                )
                const avatarPath = foundUser?.image
                  ? `/api/v1/images/${foundUser.image}`
                  : item.avatar_url || item.avatarUrl || item.avatar

                return (
                  <UserProfilePopover
                    userCode={item.userCode}
                    displayName={item.displayName}
                    avatarUrl={avatarPath}>
                    <List.Item
                      style={{
                        borderBottom: 'none',
                        padding: '8px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        borderRadius: 8,
                        paddingLeft: 8,
                        paddingRight: 8,
                      }}
                      className="hoverable-list-item">
                      <div
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                        }}>
                        <Avatar
                          size={40}
                          src={resolveAvatarUrl(avatarPath)}
                          style={{
                            background: colors.primary,
                            color: '#fff',
                            fontSize: 14,
                            fontWeight: 'bold',
                          }}>
                          {displayName.charAt(0)}
                        </Avatar>
                        <div
                          style={{
                            position: 'absolute',
                            bottom: -2,
                            right: -2,
                            borderRadius: '50%',
                            width: 18,
                            height: 18,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff',
                            border: '2px solid white',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                          }}>
                          <ReactionIcon
                            type={item.reactionObj?.key || 'LIKE'}
                            size={14}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                        }}>
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 15,
                            color: '#050505',
                          }}>
                          {displayName}
                        </span>
                        <span style={{ fontSize: 13, color: '#65676b' }}>
                          @{item.userCode}
                        </span>
                      </div>
                    </List.Item>
                  </UserProfilePopover>
                )
              }}
            />
          )}
        </div>
      </ReactionsModal>

      {/* Edit Post Modal */}
      <Modal
        title={
          <div
            style={{
              textAlign: 'center',
              fontSize: 18,
              fontWeight: 700,
              width: '100%',
            }}>
            Chỉnh sửa bài viết
          </div>
        }
        visible={isEditModalVisible}
        onCancel={() => {
          if (!isEditSubmitting) {
            setIsEditModalVisible(false)
          }
        }}
        footer={null}
        width={600}
        destroyOnClose
        centered
        bodyStyle={{ padding: '16px 24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* User Profile Info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              size={48}
              src={avatarSrc}
              style={{
                background: colors.primary,
                color: '#fff',
                fontSize: 18,
                fontWeight: 'bold',
              }}>
              {(formatName(post.display_name) || 'U').charAt(0)}
            </Avatar>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 4,
                  fontWeight: 600,
                  fontSize: 15,
                  color: '#050505',
                }}>
                <span>{formatName(post.display_name)}</span>
                {editFeeling && (
                  <span
                    style={{
                      fontSize: 14,
                      color: '#65676b',
                      fontWeight: 'normal',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}>
                    <span>-</span>
                    <span>{editFeeling.emoji}</span>
                    <span>
                      {editFeeling.text ||
                        `cảm thấy ${editFeeling.label?.toLowerCase()}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setEditFeeling(null)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: '#999',
                        fontSize: 12,
                        padding: '0 4px',
                      }}
                      title="Xóa cảm xúc">
                      ✕
                    </button>
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Visibility Select */}
                <Select
                  value={editVisibility}
                  onSelect={val => {
                    setEditVisibility(val)
                    if (val === 'CUSTOM') {
                      if (selectPeopleStore) {
                        selectPeopleStore.assigneePopupType = 'CUSTOM_VIEWERS'
                        if (
                          !selectPeopleStore.selectUserData['CUSTOM_VIEWERS']
                        ) {
                          selectPeopleStore.selectUserData[
                            'CUSTOM_VIEWERS'
                          ] = []
                        }
                      }
                      setShowEditSelectPeople(true)
                    }
                  }}
                  style={{ width: 130 }}
                  size="small"
                  className="nf-visibility-select"
                  bordered={false}
                  dropdownStyle={{ borderRadius: 8 }}>
                  <Select.Option value="PUBLIC">
                    <span
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <GlobalOutlined style={{ color: '#1877f2' }} /> Mọi người
                    </span>
                  </Select.Option>
                  <Select.Option value="CUSTOM">
                    <span
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <UsergroupAddOutlined style={{ color: '#1877f2' }} /> Tùy
                      chỉnh
                    </span>
                  </Select.Option>
                </Select>

                {editVisibility === 'CUSTOM' && (
                  <span
                    onClick={() => {
                      if (selectPeopleStore) {
                        selectPeopleStore.assigneePopupType = 'CUSTOM_VIEWERS'
                      }
                      setShowEditSelectPeople(true)
                    }}
                    style={{
                      fontSize: 12,
                      color: '#1877f2',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}>
                    (Sửa người xem)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* CKEditor Customized for Edit Modal */}
          <NewsFeedEditor
            value={editText}
            onChange={val => setEditText(val)}
            placeholder={`${formatName(
              post.display_name
            )} ơi, bạn muốn chỉnh sửa gì thế?`}
            minHeight="140px"
            maxHeight="320px"
            disabled={isEditSubmitting}
            userStore={userStore}
          />


          {/* Custom Viewers List */}
          {editVisibility === 'CUSTOM' &&
            selectPeopleStore?.selectUserData['CUSTOM_VIEWERS'] &&
            selectPeopleStore.selectUserData['CUSTOM_VIEWERS'].length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  padding: '4px 8px',
                  background: '#f8fafc',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                }}>
                {selectPeopleStore.selectUserData['CUSTOM_VIEWERS'].map(
                  user => (
                    <Tag
                      key={user.id}
                      closable
                      onClose={e => {
                        e.preventDefault()
                        if (selectPeopleStore) {
                          selectPeopleStore.selectUserData[
                            'CUSTOM_VIEWERS'
                          ] = selectPeopleStore.selectUserData[
                            'CUSTOM_VIEWERS'
                          ].filter(u => u.id !== user.id)
                          setEditCustomViewers(
                            selectPeopleStore.selectUserData[
                              'CUSTOM_VIEWERS'
                            ].map(u => u.id)
                          )
                        }
                      }}
                      color="blue"
                      style={{
                        borderRadius: 16,
                        fontSize: 13,
                        padding: '2px 8px',
                      }}>
                      {utils.getNameInCapitalize(
                        user.full_name ||
                          user.name_uppercase ||
                          user.displayName ||
                          user.username
                      )}
                    </Tag>
                  )
                )}
              </div>
            )}

          {/* Tagged Friends List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div
                  onClick={() => {
                    if (selectPeopleStore) {
                      selectPeopleStore.assigneePopupType = 'TAGGED_USERS'
                    }
                    setShowEditSelectPeopleTagged(true)
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    color: '#1877f2',
                    fontWeight: 600,
                    fontSize: 14,
                  }}>
                  <UsergroupAddOutlined style={{ fontSize: 16 }} />
                  Gắn thẻ bạn bè
                </div>
                <div
                  onClick={() => setShowEditFeelingModal(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    color: '#f59e0b',
                    fontWeight: 600,
                    fontSize: 14,
                  }}>
                  <SmileOutlined style={{ fontSize: 16 }} />
                  {editFeeling ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                      }}>
                      <span>{editFeeling.emoji}</span>
                      <span>{editFeeling.label}</span>
                    </span>
                  ) : (
                    'Cảm xúc'
                  )}
                </div>
              </div>
            </div>

            {selectPeopleStore?.selectUserData['TAGGED_USERS'] &&
              selectPeopleStore.selectUserData['TAGGED_USERS'].length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    padding: '4px 8px',
                    background: '#f0fdf4',
                    borderRadius: 8,
                    border: '1px solid #bbf7d0',
                  }}>
                  <span
                    style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
                    Cùng với:{' '}
                  </span>
                  {selectPeopleStore.selectUserData['TAGGED_USERS'].map(
                    user => {
                      const uId = user.username || user.code || user.id
                      return (
                        <Tag
                          key={uId}
                          closable
                          onClose={e => {
                            e.preventDefault()
                            if (selectPeopleStore) {
                              selectPeopleStore.selectUserData[
                                'TAGGED_USERS'
                              ] = selectPeopleStore.selectUserData[
                                'TAGGED_USERS'
                              ].filter(
                                u => (u.username || u.code || u.id) !== uId
                              )
                              setEditTaggedUsers(
                                selectPeopleStore.selectUserData[
                                  'TAGGED_USERS'
                                ].map(u => u.username || u.code || u.id)
                              )
                            }
                          }}
                          color="success"
                          style={{
                            borderRadius: 16,
                            fontSize: 13,
                            padding: '2px 8px',
                          }}>
                          {utils.getNameInCapitalize(
                            user.full_name ||
                              user.name_uppercase ||
                              user.displayName ||
                              user.username
                          )}
                        </Tag>
                      )
                    }
                  )}
                </div>
              )}
          </div>

          {/* Media Files section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#050505' }}>
                Ảnh/Video/Tài liệu đính kèm
              </span>
              <button
                type="button"
                onClick={() => editFileInputRef.current?.click()}
                disabled={isEditSubmitting}
                style={{
                  border: 'none',
                  background: '#e4e6eb',
                  color: '#050505',
                  padding: '6px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                <PictureOutlined /> Thêm file
              </button>
              <input
                type="file"
                ref={editFileInputRef}
                style={{ display: 'none' }}
                multiple
                onChange={handleEditFileSelect}
              />
            </div>

            {editMediaFiles.length > 0 && (
              <ComposerMediaEditor
                mediaFiles={editMediaFiles}
                disabled={isEditSubmitting}
                onClearAll={() => {
                  editMediaFiles.forEach(m => {
                    if (m.isNew && m.preview) URL.revokeObjectURL(m.preview)
                  })
                  setEditMediaFiles([])
                }}
                onRemoveItem={media => {
                  if (media.isNew && media.preview) {
                    URL.revokeObjectURL(media.preview)
                  }
                  setEditMediaFiles(prev =>
                    prev.filter(item => item !== media)
                  )
                }}
                onChangeCaption={(media, caption) => {
                  setEditMediaFiles(prev =>
                    prev.map(item =>
                      item === media ? { ...item, caption } : item
                    )
                  )
                }}
              />
            )}
          </div>

          {/* Upload Progress */}
          {isEditSubmitting && editUploadProgress > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: '#65676b', marginBottom: 4 }}>
                Đang tải lên các file...
              </div>
              <Progress
                percent={editUploadProgress}
                status="active"
                strokeColor={colors.primary}
              />
            </div>
          )}

          {/* Action Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              marginTop: 8,
            }}>
            <button
              type="button"
              onClick={() => setIsEditModalVisible(false)}
              disabled={isEditSubmitting}
              style={{
                border: 'none',
                background: '#e4e6eb',
                color: '#050505',
                padding: '8px 16px',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}>
              Hủy
            </button>
            <button
              type="button"
              onClick={handleEditSubmit}
              disabled={
                isEditSubmitting ||
                (!editText.trim() && editMediaFiles.length === 0)
              }
              style={{
                border: 'none',
                background:
                  isEditSubmitting ||
                  (!editText.trim() && editMediaFiles.length === 0)
                    ? '#cbd5e1'
                    : colors.primary,
                color: '#fff',
                padding: '8px 24px',
                borderRadius: 6,
                cursor:
                  isEditSubmitting ||
                  (!editText.trim() && editMediaFiles.length === 0)
                    ? 'not-allowed'
                    : 'pointer',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
              {isEditSubmitting ? 'Đang lưu...' : 'Lưu'}
            </button>
          </div>
        </div>
      </Modal>

      {showEditSelectPeople && (
        <SelectPeoplePopup
          isVisibleSelectPeoplePopup={showEditSelectPeople}
          handleSubmitSelectUser={() => {
            if (
              selectPeopleStore &&
              selectPeopleStore.selectUserData['CUSTOM_VIEWERS']
            ) {
              const viewers = selectPeopleStore.selectUserData[
                'CUSTOM_VIEWERS'
              ].map(u => u.id)
              setEditCustomViewers(viewers)
            }
            setShowEditSelectPeople(false)
          }}
          handleCancelSelectUser={() => setShowEditSelectPeople(false)}
          title="Chọn người được xem bài viết"
          onlySelectOne={false}
          disableSelectDepartment={true}
        />
      )}
      {showEditSelectPeopleTagged && (
        <SelectPeoplePopup
          isVisibleSelectPeoplePopup={showEditSelectPeopleTagged}
          handleSubmitSelectUser={() => {
            if (
              selectPeopleStore &&
              selectPeopleStore.selectUserData['TAGGED_USERS']
            ) {
              const users = selectPeopleStore.selectUserData[
                'TAGGED_USERS'
              ].map(u => u.username || u.code || u.id)
              setEditTaggedUsers(users)
            }
            setShowEditSelectPeopleTagged(false)
          }}
          handleCancelSelectUser={() => setShowEditSelectPeopleTagged(false)}
          title="Gắn thẻ bạn bè"
          onlySelectOne={false}
          disableSelectDepartment={true}
        />
      )}
      <FeelingSelectModal
        visible={showEditFeelingModal}
        onClose={() => setShowEditFeelingModal(false)}
        onSelectFeeling={setEditFeeling}
        selectedFeeling={editFeeling}
      />
    </PostCardContainer>
  )
}

export default inject(
  'newsFeedStore',
  'authenticationStore',
  'userStore',
  'selectPeopleStore'
)(observer(PostCard))
